import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { LojaPageContainer, LojaDataGrid } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";
import { formatCurrency } from "helpers/utils";
import { ITipoPagamento } from "interfaces/payment";
import { getAllPaymentActive, updatePaymentAndTaxa } from "services/payments";
import TaxaPagamentoModal from "./components/TaxaPagamentoModal";
import { TAXAS_ACCENT, taxasToolbarSx } from "./taxas.theme";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight="bold" color={accent ? TAXAS_ACCENT : "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function DefinicaoTaxas() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<ITipoPagamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<ITipoPagamento | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, status } = await getAllPaymentActive();
      if (status !== 200) {
        setMessage(dispatch, {
          open: true,
          text: "Erro ao carregar formas de pagamento.",
          severity: "error",
        });
        return;
      }
      setRows(data);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text: error instanceof Error ? error.message : "Erro ao carregar os dados.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const ativos = rows.filter((r) => r.status).length;
    const percentual = rows.filter((r) => r.taxaIsPercentage).length;
    return { total: rows.length, ativos, inativos: rows.length - ativos, percentual };
  }, [rows]);

  const openEdit = (row: ITipoPagamento) => {
    setEditing({ ...row });
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const handleChange = (field: keyof ITipoPagamento, value: unknown) => {
    setEditing((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { status } = await updatePaymentAndTaxa(editing.id, editing);
      if (status !== 200) {
        setMessage(dispatch, {
          open: true,
          text: "Erro ao atualizar os dados.",
          severity: "error",
        });
        return;
      }
      setRows((prev) => prev.map((row) => (row.id === editing.id ? editing : row)));
      setMessage(dispatch, {
        open: true,
        text: "Taxa atualizada com sucesso.",
        severity: "success",
      });
      handleClose();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text: error instanceof Error ? error.message : "Erro ao salvar.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef<ITipoPagamento>[] = useMemo(
    () => [
      {
        field: "tipoPagamento",
        headerName: "Forma de pagamento",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.tipoPagamento}
          </Typography>
        ),
      },
      {
        field: "taxa_transacao",
        headerName: "Taxa",
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.taxaIsPercentage
              ? `${params.value}%`
              : formatCurrency(params.value)}
          </Typography>
        ),
      },
      {
        field: "taxaIsPercentage",
        headerName: "Tipo taxa",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Percentual" : "Fixo"}
            variant="outlined"
            color={params.value ? "info" : "default"}
          />
        ),
      },
      {
        field: "icon",
        headerName: "Ícone",
        width: 100,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Box
            component="img"
            src={params.value}
            alt=""
            sx={{ height: 36, width: "auto", objectFit: "contain" }}
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Ativo" : "Inativo"}
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 64,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Tooltip title="Editar taxa">
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <LojaPageContainer
      title="Definição de taxas"
      subtitle="Configure taxas de transação e status das formas de pagamento."
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini label="Formas cadastradas" value={stats.total} accent />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Ativas" value={stats.ativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Inativas" value={stats.inativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Taxa percentual" value={stats.percentual} />
        </Grid>
      </Grid>

      <MDBox sx={taxasToolbarSx}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaymentsOutlinedIcon sx={{ color: TAXAS_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            {rows.length > 0
              ? `${rows.length} forma${rows.length === 1 ? "" : "s"} de pagamento`
              : "Nenhuma forma de pagamento"}
          </MDTypography>
        </Stack>
      </MDBox>

      {!loading && rows.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <PercentOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma forma de pagamento
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Não há tipos de pagamento cadastrados no momento.
          </MDTypography>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          onRowClick={(params) => openEdit(params.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "grey.100",
              fontWeight: 700,
            },
          }}
        />
      )}

      <TaxaPagamentoModal
        open={openModal}
        row={editing}
        saving={saving}
        onClose={handleClose}
        onSave={() => void handleSave()}
        onChange={handleChange}
      />
    </LojaPageContainer>
  );
}
